import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Presentation, Sparkles, FileText, Download, 
  Image as ImageIcon, Palette, Zap, CheckCircle 
} from 'lucide-react';

const DigiSlidesPro = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "AI-Powered Content",
      description: "Generate professional slide content with AI assistance"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Beautiful Templates",
      description: "Choose from dozens of professionally designed templates"
    },
    {
      icon: <ImageIcon className="w-8 h-8" />,
      title: "Stock Images",
      description: "Access millions of free stock photos integrated directly"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Multiple Formats",
      description: "Export to PowerPoint (PPTX), PDF, or Google Slides"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Fast & Easy",
      description: "Create presentations in minutes, not hours"
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: "Export Anywhere",
      description: "Download and present from any device"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-16">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl">
              <Presentation className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Digi<span className="text-purple-600">Slides</span> Pro
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Create stunning presentations with AI-powered content generation and professional templates
          </p>

          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/digi-slides/editor')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Creating
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="px-8 py-6 text-lg rounded-full border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              Explore Other Apps
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100"
            >
              <div className="text-purple-600 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Preview Section */}
        <div className="bg-white rounded-3xl shadow-2xl p-12 mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8">
            What You'll Get
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">AI Content Generation</h4>
                  <p className="text-gray-600 text-sm">Enter your topic and let AI create professional slide content</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Templates</h4>
                  <p className="text-gray-600 text-sm">Choose from business, education, creative, and more</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Drag & Drop Editor</h4>
                  <p className="text-gray-600 text-sm">Intuitive interface for easy customization</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Stock Image Library</h4>
                  <p className="text-gray-600 text-sm">Millions of high-quality images at your fingertips</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Export Options</h4>
                  <p className="text-gray-600 text-sm">PowerPoint, PDF, and Google Slides compatible</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Cloud Storage</h4>
                  <p className="text-gray-600 text-sm">Access your presentations from anywhere</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Be the First to Know
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Start creating beautiful presentations with AI assistance
          </p>
          <Button
            onClick={() => navigate('/digi-slides/editor')}
            className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
          >
            Create Your First Presentation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DigiSlidesPro;
